/**
 * XmlFeedProvider — feed konfig + aktif sync durumu paylaşımlı state.
 *
 * Sayfa değişse bile (Ürünler → Sync Geçmişi) aynı feed doc'unu ve aktif
 * sync durumunu kullanır. Sync bittiğinde tek noktadan toast fırlatır —
 * "başka sekmede sync başlattım, unutunca haberdar olayım" senaryosu için.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const XmlFeedContext = createContext(null);

const IN_PROGRESS = new Set(["queued", "downloading", "parsing", "writing"]);

export function XmlFeedProvider({ children }) {
    const [feed, setFeed] = useState(null);        // null=loading | false=none | obj
    const [jobStatus, setJobStatus] = useState(null);
    const pollingRef = useRef(null);
    const prevStatusRef = useRef(null);

    const loadFeed = useCallback(async () => {
        try {
            const { data } = await api.get("/xml/feed");
            setFeed(data);
        } catch {
            setFeed(false);
        }
    }, []);

    const pollStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/xml/feed/sync/status");
            setJobStatus(data);

            // Terminal state geçişi: toast fırlat
            const prev = prevStatusRef.current;
            const curr = data.status;
            if (prev && IN_PROGRESS.has(prev) && !IN_PROGRESS.has(curr)) {
                if (curr === "success") {
                    toast.success(`Sync tamamlandı — ${data.total_written ?? 0} ürün yazıldı.`);
                    loadFeed();
                } else if (curr === "failed") {
                    toast.error(`Sync başarısız: ${data.error?.slice(0, 80) || "bilinmeyen hata"}`);
                } else if (curr === "cancelled") {
                    toast(`Sync iptal edildi.`);
                }
            }
            prevStatusRef.current = curr;

            // Polling ayarla
            if (IN_PROGRESS.has(curr)) {
                if (!pollingRef.current) {
                    pollingRef.current = setInterval(pollStatus, 2000);
                }
            } else {
                if (pollingRef.current) {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                }
            }
        } catch { /* sessiz — polling devam eder */ }
    }, [loadFeed]);

    useEffect(() => {
        loadFeed();
        pollStatus();
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [loadFeed, pollStatus]);

    return (
        <XmlFeedContext.Provider value={{ feed, jobStatus, refresh: loadFeed, pollStatus }}>
            {children}
        </XmlFeedContext.Provider>
    );
}

export const useXmlFeed = () => {
    const ctx = useContext(XmlFeedContext);
    if (!ctx) throw new Error("useXmlFeed must be used within XmlFeedProvider");
    return ctx;
};

export { IN_PROGRESS };
